import ApplicationController from '../application_controller';
import Fetchers from 'core/fetchers';

export default class extends ApplicationController {
  static targets = [ 'address', 'button', 'expressCheckout', 'fieldset', 'hiddenField',
                     'modalContent', 'paymentSection', 'validatedAddress' ];

  static values = { validatedAddress: Object };

  static outlets = ['containers--dialog-container', 'checkout--checkout', 'checkout--validity--fieldset', 'rudderstack--checkout-step-viewed'];

  beginAddressVerification() {
    this.updateFieldsetValidityValues()
    this.forceValidationOfShippingAndContactInputs()

    try {
      const data = this.checkoutCheckoutOutlet.serializeFormData();

      dataLayer.push({
        'event': 'checkout_step_1_completed',
        'user__email': data.checkout.bill_email,
        'user__phone': data.checkout.bill_phone,
        'has_sms_marketing_opt_in': Boolean(data.subscribe.email_subscribe),
        'has_email_marketing_opt_in': Boolean(data.subscribe.attentive_subscribe)
      });
    }

    catch (err) {
      console.error(err);
    }

    if (!this.shipAddressAndContactInfoValid()) {
      this.focusFirstInvalidShippingOrContactField()
      return;
    }

    if (this.addressCanBeValidated()) {
      this.fetchModalData();
    } else {
      this.savedAddressUpdates();
      this.beginPaymentPhase();
    }
  }

  savedAddressUpdates() {
    this.validatedAddressValue = this.hiddenFieldTarget.value = this.checkoutCheckoutOutlet.verifiedSavedAddress();
  }

  beginPaymentPhase() {
    this.changeExpressCheckoutSectionVisibility();
    this.collapseContactAndShippingFieldsets();
    this.checkoutCheckoutOutlet.updateSameAsShippingRadioText();
    this.changePaymentSectionVisibility(true);
    this.changeValidateButtonVisibility(false);
    this.changePlaceOrderButtonVisibility(true);
    this.checkoutCheckoutOutlet.setCheckoutType();
    this.checkoutCheckoutOutlet.setBillingAddressFields();
    if (this.checkoutCheckoutOutlet.locationValue !== 'storefront') {
      this.rudderstackCheckoutStepViewedOutlets
        .find((outlet) => outlet.stepValue === 'payment')
        .track();
    }
    this.closeDialogContainer();
    this.scrollToShippingSection();
  }

  confirmAddress(e) {
    e.preventDefault()
    this.setValidatedAddress();
    this.setHiddenField();
    this.beginPaymentPhase();
  }

  continue(e) {
    e.preventDefault()
    this.beginPaymentPhase();
  }

  abort(e) {
    e.preventDefault()
    this.containersDialogContainerOutlet.close();
  }

  restartAddressValidationFlow() {
    this.changePaymentSectionVisibility(false);
    this.changePlaceOrderButtonVisibility(false);
    this.changeValidateButtonVisibility(true);
    this.expandContactAndShippingFieldsets();
  }

  addressCanBeValidated() {
    if (this.checkoutCheckoutOutlet.addressVerifiable()) {
      return !this.checkoutCheckoutOutlet.hasVerifiedSavedAddressSelected()
    }
  }

  shipAddressAndContactInfoValid() {
    return this.checkoutValidityFieldsetOutlets
      .every(fieldsetOutlet => fieldsetOutlet.valid());
  }

  focusFirstInvalidShippingOrContactField() {
    this.checkoutCheckoutOutlet.checkoutValidityBaseOutlets.every((outlet) => {
      if (outlet.valid()) return true;

      outlet.fieldTarget.focus();
      return false;
    });
  }

  forceValidationOfShippingAndContactInputs() {
    this.checkoutCheckoutOutlet.checkoutValidityBaseOutlets.forEach(outlet => {
      if (['contact-info', 'shipping-address'].includes(outlet.parentFieldset())) outlet.updateValidity();
    });
  }

  changePaymentSectionVisibility(show = true) {
    this.paymentSectionTargets.forEach(outlet => {
      if (show) {
        outlet.classList.remove('hide')
      } else {
        outlet.classList.add('hide')
      }
    })
  }

  changeValidateButtonVisibility(show = true) {
    if (show) {
      this.buttonTarget.classList.remove('hide')
    } else {
      this.buttonTarget.classList.add('hide')
    }
  }

  changePlaceOrderButtonVisibility(show = true) {
    if (show) {
      this.checkoutCheckoutOutlet.placeOrderButtonTarget.classList.remove('hidden')
    } else {
      this.checkoutCheckoutOutlet.placeOrderButtonTarget.classList.add('hidden')
    }
  }

  closeDialogContainer() {
    if (this.addressCanBeValidated()) this.containersDialogContainerOutlet.close();
  }

  updateFieldsetValidityValues() {
    this.checkoutValidityFieldsetOutlets.forEach((outlet) => {
      outlet.updateValidity();
    });
  }

  changeExpressCheckoutSectionVisibility(show) {
    if (!this.hasExpressCheckoutTarget) return;

    if (show) {
      this.expressCheckoutTarget.classList.remove('hidden');
    } else {
      this.expressCheckoutTarget.classList.add('hidden');
    }
  }

  collapseContactAndShippingFieldsets() {
    this.checkoutValidityFieldsetOutlets.forEach((outlet) => {
      outlet.collapse();
    });
  }

  expandContactAndShippingFieldsets() {
    this.checkoutValidityFieldsetOutlets.forEach((outlet) => {
      if (['checkout[ship_firstname]', 'checkout[shipping_method_id]'].includes(outlet.firstFieldName())) {
        outlet.expand();
      }
    });
  }

  scrollToShippingSection() {
    this.paymentSectionTarget.scrollIntoView();
  }

  fetchModalData() {
    this.checkoutCheckoutOutlet.openLoadingOverlay();

    Fetchers.fetchJSON(
      '/checkout/address_validation',
      'POST',
      JSON.stringify(this.checkoutCheckoutOutlet.serializeFormData()),
    )
      .then(Fetchers.parseResponse)
      .then((res) => {
        const data = res.data;
        this.openModal(data)
      })
      .catch((error) => {
        console.log(error);
        this.checkoutCheckoutOutlet.closeLoadingOverlay();
      });
  }

  openModal(data) {
    this.checkoutCheckoutOutlet.closeLoadingOverlay();
    this.validatedAddressValue = data.address_validation_data;

    if (data.address_validation_data.skip_validation) {
      this.setHiddenField();
      this.beginPaymentPhase();
      return;
    }

    this.modalContentTarget.innerHTML = data.html
    this.containersDialogContainerOutlet.open();
    // Skip tracking on Storefront
    if (this.checkoutCheckoutOutlet.locationValue !== 'storefront') {
      this.rudderstackCheckoutStepViewedOutlets
        .find((outlet) => outlet.stepValue === 'verify_address')
        .track();
    }
  }

  setValidatedAddress() {
    const address = this.validatedAddressValue.validated_address
    this.checkoutCheckoutOutlet.shipAddress1Target.value = address.line1;
    this.checkoutCheckoutOutlet.shipAddress2Target.value = address.line2;
    this.checkoutCheckoutOutlet.shipCityTarget.value = address.city;
    this.checkoutCheckoutOutlet.shipStateTarget.value = address.region;
    this.checkoutCheckoutOutlet.shipZipCodeTarget.value = address.postalCode;
    this.checkoutCheckoutOutlet.shipCountryTarget.value = address.countryId;
  }

  setHiddenField() {
    this.hiddenFieldTarget.value = JSON.stringify(this.validatedAddressValue.original_payload);
  }
}
